import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useEntitlement } from "@/lib/entitlement";
import { isProActive, isOwner } from "@/lib/featureAccess";
import { useFounding500, trackBillingEvent, trackFoundingCounterViewed, TRIAL_COPY, PRICING } from "@/lib/billingClient";
import { Founding500Counter } from "@/components/Founding500Counter";

/**
 * RC-1 Freemium three-tier pricing page. `/pricing` route.
 * Pro pricing is the Phase-2 commercial config: $20/mo · $200/yr, 7-day
 * free trial, with the Founding 500 50% campaign while allocation remains.
 */
export default function Pricing() {
  const nav = useNavigate();
  const ent = useEntitlement();
  const f500 = useFounding500(60000);
  const founding = f500.data;
  const showOwnerBanner = isOwner(ent);
  const isPro = isProActive(ent) && !showOwnerBanner;
  const isEnterprise = Boolean(ent && ent.isEnterprise) && !showOwnerBanner;
  const foundingActive = Boolean(founding?.offer_active && founding?.remaining > 0);
  // Same computation as SubscribeModal.jsx — the Founding annual savings
  // ($200 → $100 = $100) differ from the regular annual savings ($40), so
  // this must never be hardcoded once the Founding offer is active.
  const annualSavings = foundingActive
    ? `$${PRICING.pro.annual.amount - PRICING.founding.annual.amount}`
    : PRICING.pro.annual.savings;

  useEffect(() => {
    trackBillingEvent("pricing_viewed");
  }, []);

  useEffect(() => {
    if (foundingActive) trackFoundingCounterViewed("pricing", founding);
  }, [foundingActive, founding]);

  const goSubscribe = (billing) => {
    trackBillingEvent("trial_cta_clicked", { plan: "pro", cadence: billing, founding: foundingActive });
    nav(`/subscribe?plan=pro&billing=${billing}`);
  };

  return (
    <div data-testid="pricing-page" className="min-h-screen bg-neutral-950 text-neutral-100 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold mb-3">Pricing</h1>
          <p className="text-neutral-400 max-w-xl mx-auto">
            RigSentinel is free for converters and 8 essential calculators.
            Unlock the full engineering suite with Pro.
          </p>
        </div>

        {showOwnerBanner && (
          <div data-testid="pricing-owner-banner" className="mx-auto mb-8 max-w-xl rounded-lg border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 text-center py-3 px-4">
            You already have full access as the owner.
          </div>
        )}

        {!foundingActive && (
          <div className="mb-6">
            <Founding500Counter loading={f500.loading} error={f500.error} data={founding} compact />
          </div>
        )}
        {foundingActive && (
          <div data-testid="founding500-panel" className="mx-auto mb-10 max-w-3xl rounded-2xl border-2 border-amber-400/60 bg-gradient-to-b from-amber-500/10 to-transparent p-6 text-center shadow-[0_0_60px_-20px_rgba(251,191,36,0.6)]">
            <div className="text-xs font-black tracking-[0.3em] text-amber-400 mb-2">FOUNDING 500</div>
            <div className="mb-3">
              <Founding500Counter loading={f500.loading} error={f500.error} data={founding} />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold mb-1">50% off for the first 500 subscribers</h2>
            <div className="text-emerald-400 font-semibold text-sm mb-3">7 DAYS FREE</div>
            <div className="flex items-center justify-center gap-6 mb-3">
              <div>
                <div className="text-2xl font-bold" data-testid="founding500-monthly-price">USD $10<span className="text-sm font-normal text-neutral-400">/month</span></div>
                <div className="text-xs text-neutral-500 line-through">normally $20</div>
              </div>
              <div className="text-neutral-600">or</div>
              <div>
                <div className="text-2xl font-bold" data-testid="founding500-annual-price">USD $100<span className="text-sm font-normal text-neutral-400">/year</span></div>
                <div className="text-xs text-neutral-500 line-through">normally $200</div>
              </div>
            </div>
            <p className="text-xs text-neutral-400 max-w-xl mx-auto mb-4">
              Join the Founding 500 and keep 50% off your qualifying RigSentinel Pro subscription
              while the promotional discount remains attached to that subscription.
            </p>
            <button
              type="button"
              data-testid="founding500-cta"
              onClick={() => goSubscribe("monthly")}
              disabled={isPro || showOwnerBanner}
              className="rounded-lg bg-amber-500 hover:bg-amber-400 disabled:opacity-60 disabled:cursor-not-allowed text-neutral-950 font-semibold py-3 px-8 transition"
            >
              Join Founding 500 — Start 7-Day Free Trial
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Free */}
          <div data-testid="pricing-card-free" className="rounded-2xl border border-neutral-800 bg-neutral-900 p-6 flex flex-col">
            <h2 className="text-xl font-semibold mb-1">Free</h2>
            <p className="text-neutral-400 text-sm mb-4">$0/month</p>
            <ul className="space-y-2 text-sm mb-6 flex-1">
              <li>✅ Unit Converters</li>
              <li>✅ 8 essential calculators</li>
              <li>✅ Unlimited input entries</li>
              <li>🔒 100+ premium calculators locked</li>
              <li>🔒 Professionalizer locked</li>
              <li>🔒 Report generation locked</li>
            </ul>
            <button
              type="button"
              data-testid="pricing-free-cta"
              onClick={() => nav("/")}
              className="w-full rounded-lg border border-neutral-700 bg-neutral-800 hover:bg-neutral-700 text-white font-medium py-3 transition"
            >
              Get Started Free
            </button>
          </div>

          {/* Pro */}
          <div data-testid="pricing-card-pro" className="rounded-2xl border-2 border-amber-400/50 bg-neutral-900 p-6 flex flex-col shadow-[0_0_40px_-15px_rgba(251,191,36,0.5)]">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-xl font-semibold">Pro</h2>
              <span className="text-xs rounded-full bg-amber-500 text-neutral-950 px-2 py-0.5 font-bold">MOST POPULAR</span>
            </div>
            <p className="text-neutral-400 text-sm mb-1">From {foundingActive ? "USD $10" : "USD $20"}/month</p>
            <p className="text-emerald-400 text-xs font-semibold mb-4" data-testid="pricing-pro-trial-badge">7-Day Free Trial on all Pro plans</p>
            <ul className="space-y-2 text-sm mb-6 flex-1">
              <li>✅ Everything in Free</li>
              <li>✅ All 100+ calculators unlocked</li>
              <li>✅ Professionalizer unlocked</li>
              <li>✅ Professional reports unlocked</li>
              <li>✅ Unlimited outputs</li>
            </ul>

            <div className="text-sm text-neutral-400 mb-1">Monthly:{" "}
              <span className="text-white font-semibold" data-testid="pricing-pro-monthly-price">
                {foundingActive ? "USD $10/month" : "USD $20/month"}
              </span>
              {foundingActive && <span className="ml-1.5 text-xs text-neutral-500 line-through">$20</span>}
            </div>
            <button
              type="button"
              data-testid="pricing-subscribe-monthly"
              onClick={() => goSubscribe("monthly")}
              disabled={isPro || showOwnerBanner}
              className="w-full rounded-lg bg-amber-500 hover:bg-amber-400 disabled:opacity-60 disabled:cursor-not-allowed text-neutral-950 font-semibold py-3 transition mb-4"
            >
              Start 7-Day Free Trial — {foundingActive ? "USD $10" : "USD $20"}/month
            </button>

            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-sm text-neutral-400">Annual:{" "}
                <span className="text-white font-semibold" data-testid="pricing-pro-annual-price">
                  {foundingActive ? "USD $100/year" : "USD $200/year"}
                </span>
                {foundingActive && <span className="ml-1.5 text-xs text-neutral-500 line-through">$200</span>}
              </span>
              <span data-testid="pricing-pro-save-badge" className="text-xs rounded-full bg-emerald-500 text-neutral-950 px-2 py-0.5 font-bold">Save {annualSavings}</span>
            </div>
            <button
              type="button"
              data-testid="pricing-subscribe-annual"
              onClick={() => goSubscribe("annual")}
              disabled={isPro || showOwnerBanner}
              className="w-full rounded-lg bg-amber-500 hover:bg-amber-400 disabled:opacity-60 disabled:cursor-not-allowed text-neutral-950 font-semibold py-3 transition"
            >
              Start 7-Day Free Trial — {foundingActive ? "USD $100" : "USD $200"}/year
            </button>
            <p className="text-xs text-neutral-500 mt-3" data-testid="pricing-pro-trial-copy">
              {TRIAL_COPY} Annual saves {annualSavings} vs 12 monthly payments.
            </p>
          </div>

          {/* Enterprise */}
          <div data-testid="pricing-card-enterprise" className="rounded-2xl border border-neutral-800 bg-neutral-900 p-6 flex flex-col">
            <h2 className="text-xl font-semibold mb-1">Enterprise</h2>
            <p className="text-neutral-400 text-sm mb-4">From USD $250/month</p>
            <ul className="space-y-2 text-sm mb-6 flex-1">
              <li>✅ Everything in Pro</li>
              <li>✅ Up to 10 users</li>
              <li>✅ Admin dashboard</li>
              <li>✅ User management</li>
              <li>✅ Priority support</li>
              <li>✅ Custom branding</li>
            </ul>

            <div className="text-sm text-neutral-400 mb-1">Monthly: <span className="text-white font-semibold" data-testid="pricing-enterprise-monthly-price">USD $250/month</span></div>
            <button
              type="button"
              data-testid="pricing-enterprise-subscribe-monthly"
              onClick={() => nav("/subscribe?plan=enterprise&billing=monthly")}
              disabled={isEnterprise || showOwnerBanner}
              className="w-full rounded-lg border border-neutral-600 bg-neutral-800 hover:bg-neutral-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3 transition mb-4"
            >
              Subscribe Monthly — USD $250/month
            </button>

            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-sm text-neutral-400">Annual: <span className="text-white font-semibold" data-testid="pricing-enterprise-annual-price">USD $2,500/year</span></span>
              <span data-testid="pricing-enterprise-save-badge" className="text-xs rounded-full bg-emerald-500 text-neutral-950 px-2 py-0.5 font-bold">Save $500</span>
            </div>
            <button
              type="button"
              data-testid="pricing-enterprise-subscribe-annual"
              onClick={() => nav("/subscribe?plan=enterprise&billing=annual")}
              disabled={isEnterprise || showOwnerBanner}
              className="w-full rounded-lg border border-neutral-600 bg-neutral-800 hover:bg-neutral-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3 transition"
            >
              Subscribe Annually — USD $2,500/year
            </button>
            <a
              data-testid="pricing-contact-sales"
              href="mailto:rigsentinel@gmail.com?subject=RigSentinel%20Enterprise%20Inquiry"
              className="text-xs text-neutral-400 hover:text-white underline text-center mt-3 transition"
            >
              Questions? Contact Sales
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
